import { useState, useEffect } from 'react';
import { useI18n } from '../../../../i18n';
import { request } from '../../../shared/services/api';
import { useRequestsConfig } from '../hooks/useRequestsConfig';
import type { DelegacionRegla, TipoSolicitud } from '../types/requests.types';

interface PersonaOption {
  id: string;
  nombre: string;
  apellido: string;
}

interface DelegacionForm {
  deleganteId: string;
  delegadoId: string;
  tipoSolicitudId: string;
  fechaInicio: string;
  fechaFin: string;
  motivo: string;
  activo: boolean;
}

const EMPTY_FORM: DelegacionForm = {
  deleganteId: '',
  delegadoId: '',
  tipoSolicitudId: '',
  fechaInicio: new Date().toISOString().slice(0, 10),
  fechaFin: '',
  motivo: '',
  activo: true,
};

export function DelegacionesTab() {
  const { t } = useI18n();
  const { items, loading, error, crear, actualizar, eliminar, refetch } =
    useRequestsConfig<DelegacionRegla>('/requests/delegaciones');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<DelegacionForm>(EMPTY_FORM);
  const [personas, setPersonas] = useState<PersonaOption[]>([]);
  const [tipos, setTipos] = useState<TipoSolicitud[]>([]);

  useEffect(() => {
    request<{ data: PersonaOption[] }>('/apps/personas')
      .then((res) => setPersonas(res.data))
      .catch(() => setPersonas([]));
    request<{ data: TipoSolicitud[] }>('/requests/tipos')
      .then((res) => setTipos(res.data))
      .catch(() => setTipos([]));
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (item: DelegacionRegla) => {
    setEditId(item.id);
    setForm({
      deleganteId: item.deleganteId,
      delegadoId: item.delegadoId,
      tipoSolicitudId: item.tipoSolicitudId ?? '',
      fechaInicio: item.fechaInicio.slice(0, 10),
      fechaFin: item.fechaFin ? item.fechaFin.slice(0, 10) : '',
      motivo: item.motivo ?? '',
      activo: item.activo,
    });
    setShowModal(true);
  };

  const close = () => setShowModal(false);

  const save = async () => {
    const payload = {
      ...form,
      tipoSolicitudId: form.tipoSolicitudId || null,
      fechaFin: form.fechaFin || null,
      motivo: form.motivo || null,
    };
    if (editId) {
      await actualizar(editId, payload);
    } else {
      await crear(payload);
    }
    close();
  };

  const handleDelete = async (id: string) => {
    await eliminar(id);
    refetch();
  };

  const personaName = (p?: { nombre: string; apellido: string }) =>
    p ? `${p.nombre} ${p.apellido}` : '-';

  if (loading) {
    return <div style={{ padding: 32, color: '#6B7280' }}>{t.requests.cargando}</div>;
  }

  if (error) {
    return <div style={{ padding: 32, color: '#EF4444' }}>{error}</div>;
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 4,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #D1D5DB',
    fontSize: 14,
    outline: 'none',
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F3864', margin: 0 }}>
          {t.requests?.delegaciones ?? 'Delegaciones'}
        </h3>
        <button
          onClick={openNew}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#1F3864',
            color: '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {t.requests?.nuevaDelegacion ?? 'Nueva delegaci\u00f3n'}
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.delegante ?? 'Delegante'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.delegado ?? 'Delegado'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.tipoSolicitud ?? 'Tipo solicitud'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.fechaInicio ?? 'Fecha inicio'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.fechaFin ?? 'Fecha fin'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.motivo ?? 'Motivo'}
            </th>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.activo ?? 'Activo'}
            </th>
            <th
              style={{
                textAlign: 'right',
                padding: '12px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: '#6B7280',
              }}
            >
              {t.requests?.acciones ?? 'Acciones'}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>{personaName(item.delegante)}</td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>{personaName(item.delegado)}</td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                {tipos.find((tp) => tp.id === item.tipoSolicitudId)?.nombre ?? '-'}
              </td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                {item.fechaInicio.slice(0, 10)}
              </td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                {item.fechaFin ? item.fechaFin.slice(0, 10) : '-'}
              </td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>{item.motivo ?? '-'}</td>
              <td style={{ padding: '12px 16px', fontSize: 14 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: item.activo ? '#22C55E' : '#D1D5DB',
                  }}
                />
              </td>
              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                <button
                  onClick={() => openEdit(item)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2F5496',
                    cursor: 'pointer',
                    marginRight: 8,
                    fontSize: 13,
                  }}
                >
                  {t.requests?.editar ?? 'Editar'}
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#EF4444',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  {t.requests?.eliminar ?? 'Eliminar'}
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td
                colSpan={8}
                style={{
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: 14,
                }}
              >
                {t.requests?.sinDelegaciones ?? 'No hay delegaciones configuradas'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              width: 480,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F3864', margin: '0 0 20px' }}>
              {editId
                ? (t.requests?.editarDelegacion ?? 'Editar delegaci\u00f3n')
                : (t.requests?.nuevaDelegacion ?? 'Nueva delegaci\u00f3n')}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>{t.requests?.delegante ?? 'Delegante'}</label>
                <select
                  value={form.deleganteId}
                  onChange={(e) => setForm({ ...form, deleganteId: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">{t.requests?.seleccionar ?? 'Seleccionar...'}</option>
                  {personas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} {p.apellido}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>{t.requests?.delegado ?? 'Delegado'}</label>
                <select
                  value={form.delegadoId}
                  onChange={(e) => setForm({ ...form, delegadoId: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">{t.requests?.seleccionar ?? 'Seleccionar...'}</option>
                  {personas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} {p.apellido}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>
                  {t.requests?.tipoSolicitud ?? 'Tipo solicitud'} (
                  {t.requests?.opcional ?? 'opcional'})
                </label>
                <select
                  value={form.tipoSolicitudId}
                  onChange={(e) => setForm({ ...form, tipoSolicitudId: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">{t.requests?.todos ?? 'Todos los tipos'}</option>
                  {tipos.map((tp) => (
                    <option key={tp.id} value={tp.id}>
                      {tp.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>{t.requests?.fechaInicio ?? 'Fecha inicio'}</label>
                  <input
                    type="date"
                    value={form.fechaInicio}
                    onChange={(e) => setForm({ ...form, fechaInicio: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    {t.requests?.fechaFin ?? 'Fecha fin'} ({t.requests?.opcional ?? 'opcional'})
                  </label>
                  <input
                    type="date"
                    value={form.fechaFin}
                    onChange={(e) => setForm({ ...form, fechaFin: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>{t.requests?.motivo ?? 'Motivo'}</label>
                <textarea
                  value={form.motivo}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  id="delegacion-activo"
                />
                <label htmlFor="delegacion-activo" style={{ fontSize: 14, color: '#374151' }}>
                  {t.requests?.activo ?? 'Activo'}
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={close}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.requests?.cancelar ?? 'Cancelar'}
              </button>
              <button
                onClick={save}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#1F3864',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t.common.guardar}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
